import { singleton, inject } from "tsyringe";
import { BackendClient } from "./backend-client";
import type { QueryParameter } from './query-parameter.interface';
import type { SortConfig } from './sort-config.types';

/**
 * Request payload for the axis-data endpoint.
 * Defines parameters for cursor-based pagination on entity axes.
 */
export interface AxisDataRequest {
    /** Entity type to query (e.g., 'employee', 'project') */
    entityType: string;
    /** Reference UID (shortId) for cursor positioning. Null = start from first entity. */
    referenceUid: string | null;
    /** Offset from reference position (negative = before, positive = after) */
    offset: number;
    /** Maximum number of items to fetch */
    limit: number;
    /** Sort configuration (strategy, path, direction) */
    sort: SortConfig;
    /** Optional query parameters for server-side filtering */
    queryParams?: QueryParameter<unknown>[];
}

/**
 * Response from the axis-data endpoint.
 * Contains fetched values with pagination boundary information.
 */
export interface AxisDataResponse {
    /** Array of [relativeIndex, shortId, title, color] tuples */
    values: [number, string, string, string | null][];
    /** True if more values exist before the returned range */
    hasMoreBefore: boolean;
    /** True if more values exist after the returned range */
    hasMoreAfter: boolean;
    /** Absolute boundaries relative to reference position */
    boundaries: { min: number; max: number };
}

/**
 * Request payload for the axis-expand endpoint.
 * Defines parameters for expanding axis boundaries to include a missing value.
 */
export interface AxisExpandRequest {
    /** Entity type to query (e.g., 'Employee', 'Project') */
    entityType: string;
    /** ShortId of the currently loaded min boundary */
    minBoundaryUid: string;
    /** ShortId of the currently loaded max boundary */
    maxBoundaryUid: string;
    /** ShortId of the missing value to include */
    missingUid: string;
    /** Sort configuration (strategy, path, direction) */
    sort: SortConfig;
    /** Optional query parameters for server-side filtering */
    queryParams?: QueryParameter<unknown>[];
}

/**
 * Response from the axis-expand endpoint.
 * Contains values to insert and metadata about where to insert them.
 */
export interface AxisExpandResponse {
    /** Array of [relativeIndex, shortId, title, color] tuples to insert */
    values: [number, string, string, string | null][];
    /** Index position where values should be inserted */
    insertAtIndex: number;
    /** Which boundary was expanded: 'before' or 'after' */
    direction: 'before' | 'after';
}

/**
 * Request payload for the axis-closest-to-today endpoint.
 * Finds the entity with the smallest absolute milestoneDelta (closest to today).
 */
export interface AxisClosestToTodayRequest {
    /** Entity type to query (e.g., 'Project') */
    entityType: string;
    /** Sort configuration — must use milestoneDelta strategy */
    sort: SortConfig;
    /** Optional query parameters for server-side filtering */
    queryParams?: QueryParameter<unknown>[];
}

/**
 * Response from the axis-closest-to-today endpoint.
 * Contains the closest entity and an anchor 2 positions above it for viewport positioning.
 */
export interface AxisClosestToTodayResponse {
    /** ShortId of the entity with MIN(ABS(delta)), or null if no entities exist */
    closestUid: string | null;
    /** 0-based sort position of the closest entity */
    closestPosition: number;
    /** ShortId of the entity 2 positions above closestUid (for viewport anchor), or null if no entities exist */
    anchorUid: string | null;
    /** 0-based position of the anchor entity: max(0, closestPosition - 2) */
    anchorPosition: number;
}

/**
 * API client for the axis-data and axis-expand endpoints.
 * Handles cursor-based pagination and on-demand expansion requests for entity axes.
 */
@singleton()
export class AxisDataClient {

    private static readonly ENDPOINT = '/api/v1/axis-data';
    private static readonly EXPAND_ENDPOINT = '/api/v1/axis-expand';
    private static readonly CLOSEST_TO_TODAY_ENDPOINT = '/api/v1/axis-closest-to-today';

    constructor(
        @inject(BackendClient) private readonly backendClient: BackendClient
    ) {}

    /**
     * Fetches axis data from the backend with cursor-based pagination.
     *
     * @param request - The axis data request parameters
     * @returns Promise resolving to the axis data response
     * @throws Error if the request fails or returns invalid data
     */
    public async fetchAxisData(request: AxisDataRequest): Promise<AxisDataResponse> {
        const body: Record<string, unknown> = {
            entityType: request.entityType,
            offset: request.offset,
            limit: request.limit,
            sort: request.sort,
        };
        if (request.referenceUid !== null) {
            body.referenceUid = request.referenceUid;
        }
        if (request.queryParams?.length) {
            body.queryParams = request.queryParams;
        }

        const response = await this.backendClient.post<AxisDataResponse>(
            AxisDataClient.ENDPOINT,
            true,
            body
        );

        if (!response) {
            throw new Error(`Axis data request failed: No response received`);
        }

        return response;
    }

    /**
     * Expands axis data to include a missing value by fetching entities
     * between the current boundary and the missing entity.
     *
     * @param request - The axis expand request parameters
     * @returns Promise resolving to the axis expand response
     * @throws Error if the request fails or returns invalid data
     */
    public async expandToValue(request: AxisExpandRequest): Promise<AxisExpandResponse> {
        const expandBody: Record<string, unknown> = {
            entityType: request.entityType,
            minBoundaryUid: request.minBoundaryUid,
            maxBoundaryUid: request.maxBoundaryUid,
            missingUid: request.missingUid,
            sort: request.sort,
        };
        if (request.queryParams?.length) {
            expandBody.queryParams = request.queryParams;
        }

        const response = await this.backendClient.post<AxisExpandResponse>(
            AxisDataClient.EXPAND_ENDPOINT,
            true,
            expandBody
        );

        if (!response) {
            throw new Error(`Axis expand request failed: No response received`);
        }

        return response;
    }

    /**
     * Fetches the entity closest to today (MIN(ABS(milestoneDelta))) and returns
     * both its position and an anchor 2 rows above it for viewport positioning.
     *
     * @param request - The closest-to-today request parameters
     * @returns Promise resolving to the closest entity and anchor data
     * @throws Error if the request fails or returns no data
     */
    public async fetchFocusTodayAnchor(request: AxisClosestToTodayRequest): Promise<AxisClosestToTodayResponse> {
        const body: Record<string, unknown> = {
            entityType: request.entityType,
            sort: request.sort,
        };
        if (request.queryParams?.length) {
            body.queryParams = request.queryParams;
        }

        const response = await this.backendClient.post<AxisClosestToTodayResponse>(
            AxisDataClient.CLOSEST_TO_TODAY_ENDPOINT,
            true,
            body
        );

        if (!response) {
            throw new Error(`Axis closest-to-today request failed: No response received`);
        }

        return response;
    }
}
